// Light shader: Creates rotating area light with segment-based lighting calculations
// Implements directional lighting with rotation and realistic light distribution
const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_seed;
uniform float u_speed;

uniform float u_scale;          // Overall scale multiplier
uniform float u_lightWidth;     // Width of light segment (0-1, scaled to canvas width)
uniform float u_intensity;      // Overall light intensity
uniform float u_lightCenterX;   // Light center X position (0-1) mapped to full scaled canvas
uniform float u_lightCenterY;   // Light center Y position (0-1) mapped to full scaled canvas
uniform float u_rotationSpeed;  // Rotation speed multiplier
uniform float u_initialRotation; // Initial rotation angle in degrees
uniform float u_falloff;        // Distance falloff factor (higher = faster decay)

uniform vec3 u_color_0;         // Primary light color
uniform vec3 u_color_1;         // Secondary light color  
uniform vec3 u_color_2;         // Accent color
uniform vec3 u_color_3;         // Background color

varying vec2 vUv;

vec4 computeSegmentLight(in vec2 position, in vec2 lightP1, in vec2 lightP2, in vec2 lightNormal) {
    vec2 P2P1 = lightP2 - lightP1;
    vec2 surfaceP1 = position - lightP1;
    vec2 surfaceP2 = position - lightP2;
    vec2 lightP3;
    
    float cosTheta1 = dot(normalize(P2P1), normalize(surfaceP1));
    float cosTheta2 = dot(normalize(-P2P1), normalize(surfaceP2)); 
    
    if(cosTheta1 < 0.0 || cosTheta2 < 0.0) {
        lightP3 = length(surfaceP1) < length(position - lightP2) ? lightP1 : lightP2;
    } else {
        float cosTheta = cosTheta1;
        lightP3 = lightP1 + length(surfaceP1 * cosTheta) * normalize(P2P1);
    }
    
    // Clamp negative values and add distance-based attenuation (scale-aware)
    float ndotl = max(0.0, dot(lightNormal, normalize(position - lightP3)));
    float dist = length((position - lightP3) / u_scale);
    float att = 1.0 / (1.0 + u_falloff * dist * dist);
    vec4 light = vec4(ndotl * att);
    
    return light;
}

void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution.xx;
    uv *= u_scale;
    
    float pi = 3.141592;
    // Convert degrees to radians
    float initialRotationRadians = u_initialRotation * pi / 180.0;
    float theta = initialRotationRadians + u_time * u_speed * u_rotationSpeed + u_seed * 0.1;
    float cs = cos(theta);
    float sn = sin(theta);
    
    // Scale light width to canvas width (u_lightWidth is 0-1, scale to canvas width)
    float scaledLightWidth = u_lightWidth * u_scale;
    
    // Initial light segment points
    vec2 lightP1 = vec2(-scaledLightWidth * 0.5, 0.0);
    vec2 lightP2 = vec2(scaledLightWidth * 0.5, 0.0);
    vec2 lightNormal = vec2(0.0, 1.0);
    
    // Apply rotation
    lightP1 = vec2(lightP1.x * cs - lightP1.y * sn, lightP1.x * sn + lightP1.y * cs);
    lightP2 = vec2(lightP2.x * cs - lightP2.y * sn, lightP2.x * sn + lightP2.y * cs);
    lightNormal = vec2(lightNormal.x * cs - lightNormal.y * sn, lightNormal.x * sn + lightNormal.y * cs);
    
    // Map center position to full scaled canvas range
    vec2 lightCenter = vec2(
        u_lightCenterX * u_scale,
        u_lightCenterY * u_scale
    );
    lightP1 += lightCenter;
    lightP2 += lightCenter;
    
    vec4 lightResult = computeSegmentLight(uv, lightP1, lightP2, lightNormal);
    
    // Apply intensity and color mixing
    float intensity = lightResult.r * u_intensity;
    vec3 col = mix(u_color_3, mix(u_color_0, u_color_1, intensity), intensity);
    col = mix(col, u_color_2, intensity * 0.3);
    
    // Tone mapping
    col = col / (1.0 + col);
    
    // Gamma correction
    col = sqrt(col);
    
    gl_FragColor = vec4(col, 1.0);
}
`;

const shader = {
  name: 'Light',
  shader: { frag },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 4,
      max: 4,
      default: ['#ff4040', '#ff6020', '#ff8010', '#1a0808'],
    },
    {
      key: 'u_scale',
      type: 'number',
      label: 'Scale',
      min: 1,
      max: 20,
      step: 0.1,
      default: 6.0,
    },
    {
      key: 'u_lightWidth',
      type: 'number',
      label: 'Light Width',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.1,
    },
    {
      key: 'u_intensity',
      type: 'number',
      label: 'Light Intensity',
      min: 0.1,
      max: 10,
      step: 0.1,
      default: 1.0,
    },
    {
      key: 'u_falloff',
      type: 'number',
      label: 'Falloff',
      min: 0,
      max: 50,
      step: 0.1,
      default: 4.0,
    },
    {
      key: 'u_lightCenterX',
      type: 'number',
      label: 'Light Center X',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },
    {
      key: 'u_lightCenterY',
      type: 'number',
      label: 'Light Center Y',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },
    {
      key: 'u_rotationSpeed',
      type: 'number',
      label: 'Rotation Speed',
      min: 0.1,
      max: 5,
      step: 0.1,
      default: 1.0,
    },
    {
      key: 'u_initialRotation',
      type: 'number',
      label: 'Initial Rotation',
      min: 0,
      max: 360,
      step: 1,
      default: 0.0,
    },
    { key: 'u_speed', type: 'number', label: 'Speed', min: 0, max: 3, step: 0.1, default: 1.0 },
    {
      key: 'u_seed',
      type: 'number',
      label: 'Random Seed',
      min: 0,
      max: 999,
      step: 1,
      default: 0,
    },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(Light, colors=#ff4040,#ff6020,#ff8010,#1a0808; u_scale=6; u_lightWidth=0.1; u_intensity=1.0; animated=true)',
    'dynamic-shader(Light, colors=#00ffff,#0080ff,#004080,#001020; u_scale=8; u_lightWidth=0.2; u_intensity=2.0; u_rotationSpeed=0.5; animated=true)',
    'dynamic-shader(Light, colors=#ff4040,#ff6020,#ff8010,#1a0808; u_scale=6; u_lightWidth=0.15; u_intensity=1.5; u_rotationSpeed=1; u_speed=1; u_seed=0; u_initialRotation=90; animated=false)',
  ],
};

export default shader;
